import {
  Form,
  FormField,
  MinLengthValidator,
  RequiredValidator,
  EmailValidator,
  MinimumValueValidator,
  MaximumValueValidator,
  PatternValidator,
} from '@thinknimble/tn-forms'
import { toSnakeCase } from '@thinknimble/tn-utils'
import options from '@/services/frequencies/options'
import { sanitizeCustomAmountInput } from '@/utils/donationAmountInput'

export class LoginForm extends Form {
  static email = new FormField({ validators: [new RequiredValidator(), new EmailValidator()] })
  static password = new FormField({ validators: [new RequiredValidator()] })
}

export class SignupForm extends Form {
  static firstName = new FormField({ validators: [new RequiredValidator()] })
  static lastName = new FormField({ validators: [new RequiredValidator()] })
  static email = new FormField({ validators: [new RequiredValidator(), new EmailValidator()] })
  static password = new FormField({
    validators: [
      new RequiredValidator(),
      new MinLengthValidator({ minLength: 8, message: 'Minimum Length of 8 required' }),
    ],
  })

  get formVal() {
    return {
      ...this.value,
    }
  }
}

export class InviteFundraiserForm extends Form {
  static firstName = new FormField({ validators: [new RequiredValidator()] })
  static lastName = new FormField({ validators: [new RequiredValidator()] })

  static employeeId = new FormField({
    validators: [
      new PatternValidator({
        pattern: /^[A-Za-z0-9]*$/,
        message: 'Employee ID must only contain letters and numbers.',
      }),
    ],
  })

  static email = new FormField({ validators: [new RequiredValidator(), new EmailValidator()] })
  static profilePicture = new FormField({
    value: null,
  })
  static homeOffice = new FormField({ validators: [] })
  static canvassingOrg = new FormField({ value: null, validators: [] })

  get formVal() {
    const value = { ...this.value }
    // API expects UUID string; omit when empty (non-super admins)
    if (!value.canvassingOrg) {
      delete value.canvassingOrg
    }
    return value
  }
}

export class RequestPasswordResetForm extends Form {
  static email = new FormField({ validators: [new RequiredValidator(), new EmailValidator()] })
}

export class PasswordResetForm extends Form {
  static password = new FormField({
    validators: [
      new RequiredValidator(),
      new MinLengthValidator({ minLength: 8, message: 'Minimum Length of 8 required' }),
    ],
  })
  static passwordConfirmation = new FormField({
    validators: [
      new RequiredValidator(),
      new MinLengthValidator({ minLength: 8, message: 'Minimum Length of 8 required' }),
    ],
  })
}

export class PersonalDonorForm extends Form {
  static firstName = new FormField({ validators: [new RequiredValidator()] })
  static lastName = new FormField({ validators: [new RequiredValidator()] })
  static birthDay = new FormField({
    validators: [
      new RequiredValidator(),
      new MinimumValueValidator({ min: 1, message: 'Please enter a minimum value of 1' }),
      new MaximumValueValidator({ max: 31, message: 'Please enter a max value of 31' }),
    ],
  })
  static birthMonth = new FormField({ validators: [new RequiredValidator()] })
  static birthYear = new FormField({ validators: [new RequiredValidator()] })
  static longitude = new FormField({})
  static latitude = new FormField({})

  get formVal() {
    return {
      ...this.value,
      dateOfBirth: `${this.value.birthYear}-${this.value.birthMonth}-${this.value.birthDay}`,
    }
  }
}

export class ContactDonorForm extends Form {
  static addressStreet = new FormField({
    validators: [new RequiredValidator({ message: 'Please select a recommended address' })],
  })
  static addressDetail = new FormField()
  static city = new FormField({ validators: [new RequiredValidator()] })
  static state = new FormField({ validators: [new RequiredValidator()] })
  static postalCode = new FormField({ validators: [new RequiredValidator()] })
  static email = new FormField({
    validators: [new RequiredValidator(), new EmailValidator()],
  })
  static mobilePhone = new FormField({
    validators: [new RequiredValidator()],
  })
  static homePhone = new FormField()
  static isEmailPreferred = new FormField({ label: 'Email', value: true })
  static isSmsPreferred = new FormField({ label: 'Text', value: false })
  static isPhoneCallPreferred = new FormField({ label: 'Phone', value: false })
  static emailUpdates = new FormField({
    label: 'Email Updates',
    value: 'Y', // Default value
  })

  static textMessages = new FormField({
    label: 'Text Messages',
    value: 'Y', // Default value
  })

  static originSource = new FormField({ validators: [new RequiredValidator()] })

  get toBillingAddressVal() {
    return {
      billingAddressStreet1: this.value.addressStreet,
      billingAddressStreet2: this.value.addressDetail,
      billingAddressCity: this.value.city,
      billingAddressState: this.value.state,
      billingAddressZip: this.value.postalCode,
      donorEmail: this.value.email,
    }
  }
}

export class PaymentDonorForm extends Form {
  static donationAmount = new FormField()
  static customAmount = new FormField()
  static sustainingFrequency = new FormField()
  static sustainingDuration = new FormField({ value: 0 })
  static levelAutorepeat = new FormField({
    validators: [new RequiredValidator({ message: 'Please select a donation frequency' })],
  })
  static agreedToTerms = new FormField({ value: true })
  static billingAddressStreet1 = new FormField({ validators: [new RequiredValidator()] })
  static billingAddressStreet2 = new FormField()
  static billingAddressCity = new FormField({ validators: [new RequiredValidator()] })
  static billingAddressState = new FormField({ validators: [new RequiredValidator()] })
  static billingAddressZip = new FormField({ validators: [new RequiredValidator()] })
  static donorEmail = new FormField({
    validators: [new RequiredValidator(), new EmailValidator()],
  })
  static donorEmailOptIn = new FormField({ value: true, validators: [new RequiredValidator()] })
  static billingNameFirst = new FormField({ validators: [new RequiredValidator()] })
  static billingNameLast = new FormField({ validators: [new RequiredValidator()] })
  static cardNumber = new FormField({
    validators: [new RequiredValidator()],
  })
  static cardCvv = new FormField({ validators: [new RequiredValidator()] })
  static cardExpDate = new FormField({
    validators: [
      new RequiredValidator(),
      new MinLengthValidator({ minLength: 9, message: 'MM / YYYY format is required' }),
    ],
  })
  static uniqueId = new FormField({ validators: [new RequiredValidator()] })
  static donorVerificationSms = new FormField({ validators: [new RequiredValidator()] })

  get formVal() {
    // PB this will likely need to be dynamic to match the object for each provider!
    // this is what it is called in BBMS
    const customAmount = sanitizeCustomAmountInput(this.value.customAmount)
    const donationAmount = this.value.donationAmount
    const otherAmount = customAmount
      ? customAmount
      : donationAmount != null && donationAmount !== ''
      ? donationAmount
      : null

    let formatted = {}
    for (const [key, value] of Object.entries(this.value)) {
      if (['donationAmount', 'customAmount', 'agreedToTerms'].includes(key)) {
        continue
      }
      if (
        /**
         * special case transformation to dot notation
         */
        [
          'billingNameFirst',
          'billingNameLast',
          'billingAddressStreet1',
          'billingAddressStreet2',
          'billingAddressCity',
          'billingAddressState',
          'billingAddressZip',
          'donorEmail',
        ].includes(key)
      ) {
        let newKey = toSnakeCase(key).replaceAll('_', '.')
        formatted[newKey] = value
      } else if (key === 'donorEmailOptIn') {
        formatted['donor.email_opt_in'] = value
      } else if (key === 'cardNumber') {
        // Remove spaces from card number, added by plugin.
        // For BBMSCheckout (Blackbaud Checkout modal) cards are tokenized in Blackbaud's
        // iframe, so this field stays empty on our side.
        formatted['card_number'] = typeof value === 'string' ? value.replace(/\s/g, '') : ''
      } else if (key === 'cardExpDate') {
        // Separate card expiration date into month and year and remove spaces.
        // Same BBMSCheckout note as above.
        if (typeof value === 'string' && value.includes('/')) {
          const [monthPart, yearPart] = value.split('/')
          formatted['card_exp_date_month'] = (monthPart || '').replace(/\s/g, '')
          formatted['card_exp_date_year'] = (yearPart || '').replace(/\s/g, '')
        } else {
          formatted['card_exp_date_month'] = ''
          formatted['card_exp_date_year'] = ''
        }
      } else if (key === 'levelAutorepeat') {
        // MK 2024-5-9: There are multiple types of recurring donations that BBMS supports.
        // The fields are either 'level_autorepeat' and 'frequency' or 'sustaining.frequency' and 'sustaining.duration'.
        // Both have been implemented here as they don't appear to cause issues for the alternative implementation
        // Formatted frequency is sending the ID. My suspicion is that the RHS should read value?.value but this is unconfirmed for now.
        formatted['frequency'] = value?.id
        formatted['sustaining.frequency'] = value?.value
        if (value?.value == options.MONTHLY) {
          formatted['level_autorepeat'] = true
          formatted['sustaining.duration'] = 0
        }
      } else {
        let newKey = toSnakeCase(key)
        formatted[newKey] = value
      }
    }
    return {
      ...formatted,
      otherAmount,
    }
  }
}

export const termsAgreementField = new FormField({
  value: true,
  validators: [new RequiredValidator()],
})
